/**********************************************************************
 * ScheduleOverlay.jsx
 * – Modale di visualizzazione / editing degli scheduling di un programma
 *********************************************************************/
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import Select from 'react-select';
import DatePicker from 'react-multi-date-picker';
import 'react-multi-date-picker/styles/backgrounds/bg-dark.css';
import { toast, Slide } from 'react-toastify';

import '../../../css/programOverlay.css';
import api from '../../../services/api';
import { buildPersonOptions, personOptionMatches } from '../../../utils/personSelect';
import { selectChangedSchedules } from '../../../utils/schedulePayload.mjs';
import PersonDetailModal from '../People/PersonDetailModal';

/* ------------------------- helper ---------------------------------- */
const toYMD = (value) => {
  if (!value) return '';
  if (typeof value === 'string') {
    return value.includes('T') ? value.slice(0, 10) : value;
  }
  if (value instanceof Date) {
    return value.toISOString().slice(0, 10);
  }
  if (typeof value === 'object') {
    if (typeof value.toDate === 'function') {
      const asDate = value.toDate();
      if (asDate instanceof Date) return asDate.toISOString().slice(0, 10);
    }
    if (typeof value.toISOString === 'function') {
      return value.toISOString().slice(0, 10);
    }
  }
  return '';
};

const avatarFor = (person) =>
  person?.imageUrl ||
  person?.image_url ||
  'https://via.placeholder.com/32';

const companyLogoFor = (company) =>
  company?.image ||
  company?.imageUrl ||
  company?.image_url ||
  'https://via.placeholder.com/32';

const formatDisplayDate = (raw) => {
  if (!raw) return '';
  const d =
    typeof raw === 'string'
      ? new Date(raw)
      : raw?.toDate?.()
      ? new Date(raw.toDate())
      : new Date(raw);
  return Number.isNaN(d.getTime())
    ? String(raw)
    : d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

const daysIT = ['lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato', 'domenica'];
const removeAccents = (value = '') => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const weekdayNumbers = {
  monday: 1,
  mon: 1,
  lun: 1,
  lunedi: 1,
  'lunedì': 1,
  tuesday: 2,
  tue: 2,
  martedi: 2,
  'martedì': 2,
  mercoledi: 3,
  'mercoledì': 3,
  mer: 3,
  wednesday: 3,
  wed: 3,
  giovedi: 4,
  'giovedì': 4,
  thursday: 4,
  thu: 4,
  gio: 4,
  venerdi: 5,
  'venerdì': 5,
  friday: 5,
  fri: 5,
  ven: 5,
  sabato: 6,
  sab: 6,
  saturday: 6,
  sat: 6,
  domenica: 7,
  dom: 7,
  sunday: 7,
  sun: 7,
};

const weekdayBaseDates = {
  1: '1970-01-05',
  2: '1970-01-06',
  3: '1970-01-07',
  4: '1970-01-01',
  5: '1970-01-02',
  6: '1970-01-03',
  7: '1970-01-04',
};

const getWeekdayFromLabel = (label = '') => {
  if (!label) return null;
  const normalized = removeAccents(label.trim().toLowerCase());
  return weekdayNumbers[normalized] ?? null;
};

const getWeekdayFromIsoDate = (isoDate = '') => {
  if (!isoDate) return null;
  const date = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  const jsDay = date.getDay();
  return jsDay === 0 ? 7 : jsDay;
};

const isDateAllowedForSchedule = (schedule, isoDate) => {
  if (!schedule?.isRecurring || !schedule.dayOfWeek || !isoDate) return true;
  const expected = getWeekdayFromLabel(schedule.dayOfWeek);
  if (!expected) return true;
  const actual = getWeekdayFromIsoDate(isoDate);
  if (!actual) return false;
  return expected === actual;
};

const getDateInputGuard = (dayOfWeek) => {
  const idx = getWeekdayFromLabel(dayOfWeek);
  if (!idx) return null;
  const baseDate = weekdayBaseDates[idx];
  if (!baseDate) return null;
  return { min: baseDate, step: 7 };
};

const timeToMinutes = (value = '') => {
  if (!value) return null;
  const [hoursStr, minutesStr = '0'] = value.split(':');
  const hours = Number(hoursStr);
  const minutes = Number(minutesStr);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
  return hours * 60 + minutes;
};

const isTimeRangeValid = (start, end) => {
  if (!start || !end) return false;
  const startMinutes = timeToMinutes(start);
  const endMinutes = timeToMinutes(end);
  if (startMinutes === null || endMinutes === null) return false;
  return startMinutes < endMinutes;
};

const slotFitsWithinParent = (slotStart, slotEnd, parentStart, parentEnd) => {
  if (!slotStart || !slotEnd || !parentStart || !parentEnd) return true;
  const slotStartMinutes = timeToMinutes(slotStart);
  const slotEndMinutes = timeToMinutes(slotEnd);
  const parentStartMinutes = timeToMinutes(parentStart);
  const parentEndMinutes = timeToMinutes(parentEnd);
  if (
    slotStartMinutes === null || slotEndMinutes === null ||
    parentStartMinutes === null || parentEndMinutes === null
  ) {
    return true;
  }
  return slotStartMinutes >= parentStartMinutes && slotEndMinutes <= parentEndMinutes;
};

const extractNumericId = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (value && typeof value === 'object') {
    if ('id' in value && Number.isFinite(Number(value.id))) return Number(value.id);
    if ('personId' in value && Number.isFinite(Number(value.personId))) return Number(value.personId);
    if ('person_id' in value && Number.isFinite(Number(value.person_id))) return Number(value.person_id);
    if ('topicId' in value && Number.isFinite(Number(value.topicId))) return Number(value.topicId);
    if ('topic_id' in value && Number.isFinite(Number(value.topic_id))) return Number(value.topic_id);
    if ('companyId' in value && Number.isFinite(Number(value.companyId))) return Number(value.companyId);
    if ('company_id' in value && Number.isFinite(Number(value.company_id))) return Number(value.company_id);
    if ('company' in value) return extractNumericId(value.company);
    if ('person' in value) return extractNumericId(value.person);
  }
  return null;
};

const toIdArray = (input) => {
  if (!Array.isArray(input)) return [];
  return input
    .map(entry => extractNumericId(entry))
    .filter(id => Number.isFinite(id) && id > 0);
};

const deriveCompanyAssignments = (slot = {}) => {
  if (Array.isArray(slot.companyAssignments) && slot.companyAssignments.length) {
    return slot.companyAssignments
      .map(assign => {
        const companyId = extractNumericId(
          assign.companyId ?? assign.company_id ?? assign.company,
        );
        const personIds = toIdArray(assign.personIds ?? assign.person_ids ?? assign.people);
        if (!companyId || !personIds.length) return null;
        return { companyId, personIds };
      })
      .filter(Boolean);
  }

  if (Array.isArray(slot.companyPeople) && slot.companyPeople.length) {
    const grouped = new Map();
    slot.companyPeople.forEach((entry) => {
      const companyId = extractNumericId(
        entry.companyId ?? entry.company_id ?? entry.company,
      );
      const personId = extractNumericId(
        entry.personId ?? entry.person_id ?? entry.person,
      );
      if (!companyId || !personId) return;
      if (!grouped.has(companyId)) grouped.set(companyId, new Set());
      grouped.get(companyId).add(personId);
    });
    return Array.from(grouped.entries()).map(([companyId, peopleSet]) => ({
      companyId,
      personIds: Array.from(peopleSet),
    }));
  }

  return [];
};

const cloneSlots = (slots = [], { keepId = true } = {}) =>
  (Array.isArray(slots) ? slots : []).map((slot) => ({
    id: keepId ? slot.id ?? null : null,
    startTime: slot.startTime || '',
    endTime: slot.endTime || '',
    note: slot.note || '',
    isEditorial: !!slot.isEditorial,
    people: toIdArray(slot.people),
    topics: toIdArray(slot.topics),
    companyAssignments: deriveCompanyAssignments(slot),
    rubricaId: slot.rubricaId || null,
    tematiche: slot.tematiche || '',
  }));

const describeScheduleLabel = (schedule) => {
  if (schedule?.isRecurring && schedule.dayOfWeek) {
    return `la ricorrenza ${schedule.dayOfWeek}`;
  }
  if (schedule?.date) {
    return `lo scheduling del ${formatDisplayDate(schedule.date)}`;
  }
  return 'lo scheduling';
};

const describeOccurrenceLabel = (occurrence, schedule) => {
  if (occurrence?.date) {
    return `l'occorrenza del ${formatDisplayDate(occurrence.date)}`;
  }
  if (schedule?.dayOfWeek) {
    return `l'occorrenza della ${schedule.dayOfWeek}`;
  }
  return "l'occorrenza";
};

/* ------------------------- component ------------------------------- */
const ScheduleOverlay = ({
  program,
  schedules,
  onClose,
  onScheduleUpdate,
  refreshProgramsList,
  peopleList,
  topicList,
  companyList = [],
  rubricaList = [],
}) => {
  const [scheduleData, setScheduleData] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const savingRef = useRef(false);
  const initialScheduleDataRef = useRef([]);
  const [detailPerson, setDetailPerson] = useState(null);
  const [formError, setFormError] = useState('');

  // Carica persona completa con qualifiche
  const handlePersonClick = async (person) => {
    try {
      const response = await api.get(`/people/${person.id}`);
      setDetailPerson(response.data);
    } catch (error) {
      console.error('Error loading person details:', error);
      setDetailPerson(person);
    }
  };
  const [viewFilter, setViewFilter] = useState('30');
  const [customRange, setCustomRange] = useState({ from: '', to: '' });
  const [jumpDate, setJumpDate] = useState('');
  const [expandedViewId, setExpandedViewId] = useState(null);
  const [chipFilterIdx, setChipFilterIdx] = useState(null);
  const [expandedIdx, setExpandedIdx] = useState(null);
  const [editViewMode, setEditViewMode] = useState('calendar'); // 'calendar' | 'list'
  const [selectedDay, setSelectedDay] = useState(() => toYMD(new Date()));
  const [viewCalendarMode, setViewCalendarMode] = useState(true);
  const [viewDay, setViewDay] = useState(() => toYMD(new Date()));

  const peopleById = useMemo(() => {
    const map = new Map();
    (peopleList || []).forEach((person) => {
      if (person?.id) map.set(person.id, person);
    });
    return map;
  }, [peopleList]);

  const topicById = useMemo(() => {
    const map = new Map();
    (topicList || []).forEach((topic) => {
      if (topic?.id) map.set(topic.id, topic);
    });
    return map;
  }, [topicList]);

  const companyById = useMemo(() => {
    const map = new Map();
    (companyList || []).forEach((company) => {
      if (company?.id) map.set(company.id, company);
    });
    return map;
  }, [companyList]);

  const peopleOptions = useMemo(
    () => buildPersonOptions(peopleList),
    [peopleList],
  );
  const topicOptions = useMemo(
    () => (topicList || [])
      .map(t => ({ value: t.id, label: t.name }))
      .filter(opt => Number.isFinite(opt.value))
      .sort((a, b) => a.label.localeCompare(b.label)),
    [topicList],
  );
  const companyOptions = useMemo(
    () => (companyList || [])
      .map(c => ({ value: c.id, label: c.name }))
      .filter(opt => Number.isFinite(opt.value))
      .sort((a, b) => a.label.localeCompare(b.label)),
    [companyList],
  );
  const rubricaOptions = useMemo(
    () => (rubricaList || [])
      .map(r => ({ value: r.id, label: r.name }))
      .filter(opt => Number.isFinite(opt.value))
      .sort((a, b) => a.label.localeCompare(b.label)),
    [rubricaList],
  );

  const peopleOptionById = useMemo(() => {
    const map = new Map();
    peopleOptions.forEach(opt => map.set(opt.value, opt));
    return map;
  }, [peopleOptions]);

  const topicOptionById = useMemo(() => {
    const map = new Map();
    topicOptions.forEach(opt => map.set(opt.value, opt));
    return map;
  }, [topicOptions]);

  const rubricaOptionById = useMemo(() => {
    const map = new Map();
    rubricaOptions.forEach(opt => map.set(opt.value, opt));
    return map;
  }, [rubricaOptions]);

  const noPeopleOptionsMessage = useCallback(
    ({ inputValue }) => inputValue
      ? 'Nessuna persona trovata'
      : 'Nessuna persona disponibile',
    [],
  );

  const hasNonEditorialSlots = useCallback((item) => {
    const slots = Array.isArray(item.timeSlots) ? item.timeSlots : [];
    const occSlots = (item.occurrences || []).flatMap(occ => occ.timeSlots || []);
    const hasAnySlot = slots.length > 0 || occSlots.length > 0;
    const hasNonEditorial = slots.some(slot => !slot.isEditorial) || occSlots.some(slot => !slot.isEditorial);
    if (!hasAnySlot) return true; // se non ci sono slot, consideralo non-editoriale per non perderlo
    return hasNonEditorial;
  }, []);

  const isEditorialOnly = useCallback((item) => {
    const slots = Array.isArray(item.timeSlots) ? item.timeSlots : [];
    const occSlots = (item.occurrences || []).flatMap(occ => occ.timeSlots || []);
    const hasAnySlot = slots.length > 0 || occSlots.length > 0;
    const hasNonEditorial = slots.some(slot => !slot.isEditorial) || occSlots.some(slot => !slot.isEditorial);
    return hasAnySlot && !hasNonEditorial;
  }, []);

  const toastOptions = useMemo(() => ({
    theme     : 'dark',
    position  : 'top-center',
    autoClose : 3500,
    transition: Slide,
  }), []);

  /* bootstrap ------------------------------------------------------- */
  useEffect(() => {
    const data = schedules
      .filter(s => +s.programId === +program.id)
      .map(s => ({
        ...s,
        date: toYMD(s.date),
        guests: s.guests ? s.guests.map(g => g.id) : [],
        topics: s.topics ? s.topics.map(t => t.id) : [],
        isRecurring: !!s.isRecurring,
        dayOfWeek: s.dayOfWeek || '',
        description: s.description || '',
        priority: s.priority ?? 0,
        blacklistDates: s.blacklistDates ? s.blacklistDates.map(b => toYMD(b.blacklistDate)) : [],
        timeSlots: Array.isArray(s.timeSlots) ? cloneSlots(s.timeSlots) : [],
        occurrences: Array.isArray(s.occurrences)
          ? s.occurrences.map(occ => ({
              ...occ,
              date: toYMD(occ.date),
              startTime: occ.startTime || '',
              endTime: occ.endTime || '',
              note: occ.note || '',
              timeSlots: Array.isArray(occ.timeSlots) ? cloneSlots(occ.timeSlots) : [],
              protagonists: toIdArray(occ.protagonists || []),
            }))
          : [],
      }));
    setScheduleData(data);
    initialScheduleDataRef.current = JSON.parse(JSON.stringify(data));
    const firstIdx = data.findIndex(item => hasNonEditorialSlots(item) && !item.isRecurring);
    const fallbackIdx = data.findIndex(item => hasNonEditorialSlots(item));
    const initialIdx = firstIdx >= 0 ? firstIdx : (fallbackIdx >= 0 ? fallbackIdx : null);
    setExpandedIdx(initialIdx);
  }, [program, schedules, hasNonEditorialSlots]);

  useEffect(() => {
    setExpandedViewId(null);
  }, [viewFilter, scheduleData.length]);

  useEffect(() => {
    setFormError('');
  }, [isEditing]);

  useEffect(() => {
    if (!isSaving) return undefined;

    const warnBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [isSaving]);

  /* Rimuovi persone eliminate dai timeSlots quando peopleList cambia */
  useEffect(() => {
    if (!peopleList || peopleList.length === 0) return;

    const validPeopleIds = new Set(peopleList.map(p => p.id));

    setScheduleData(prevData => {
      let changed = false;

      const newData = prevData.map(item => {
        // Filtra guests
        const filteredGuests = (item.guests || []).filter(id => validPeopleIds.has(id));
        if (filteredGuests.length !== (item.guests || []).length) changed = true;

        // Filtra people nei timeSlots
        const filteredTimeSlots = (item.timeSlots || []).map(slot => {
          const filteredPeople = (slot.people || []).filter(id => validPeopleIds.has(id));
          if (filteredPeople.length !== (slot.people || []).length) changed = true;
          return { ...slot, people: filteredPeople };
        });

        // Filtra people nelle occurrences
        const filteredOccurrences = (item.occurrences || []).map(occ => {
          const filteredProtagonists = (occ.protagonists || []).filter(id => validPeopleIds.has(id));
          if (filteredProtagonists.length !== (occ.protagonists || []).length) changed = true;

          const filteredOccSlots = (occ.timeSlots || []).map(slot => {
            const filteredPeople = (slot.people || []).filter(id => validPeopleIds.has(id));
            if (filteredPeople.length !== (slot.people || []).length) changed = true;
            return { ...slot, people: filteredPeople };
          });

          return { ...occ, protagonists: filteredProtagonists, timeSlots: filteredOccSlots };
        });

        return {
          ...item,
          guests: filteredGuests,
          timeSlots: filteredTimeSlots,
          occurrences: filteredOccurrences,
        };
      });

      return changed ? newData : prevData;
    });
  }, [peopleList]);

  const emitFormError = (message) => {
    if (!message) {
      setFormError('');
      return;
    }
    setFormError(message);
    toast.error(message, toastOptions);
  };

  /* filtering ------------------------------------------------------- */
  const viewFilterOptions = [
    { value: '7', label: 'ultimi 7 giorni' },
    { value: '30', label: 'ultimi 30 giorni' },
    { value: '90', label: 'ultimi 90 giorni' },
    { value: 'future', label: 'prossimi 30 giorni' },
    { value: 'upcoming90', label: 'prossimi 90 giorni' },
    { value: 'all', label: 'tutti' },
    { value: 'custom', label: 'intervallo personalizzato' },
  ];

  const startOfToday = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  }, []);

  const getTimestamp = useCallback((dateStr) => {
    if (!dateStr) return null;
    const ts = new Date(`${dateStr}T00:00:00`).getTime();
    return Number.isNaN(ts) ? null : ts;
  }, []);

  const matchesFilter = useCallback((item) => {
    if (item.isRecurring) return true;
    const ts = getTimestamp(item.date);
    if (ts === null) return true;

    if (viewFilter === 'all') return true;
    if (viewFilter === 'future') {
      const futureCutoff = startOfToday + (30 * 86400000);
      return ts >= startOfToday && ts <= futureCutoff;
    }
    if (viewFilter === 'upcoming90') {
      const futureCutoff = startOfToday + (90 * 86400000);
      return ts >= startOfToday && ts <= futureCutoff;
    }
    if (viewFilter === 'custom') {
      const fromTs = customRange.from ? getTimestamp(customRange.from) : null;
      const toTs = customRange.to ? getTimestamp(customRange.to) : null;
      if (fromTs && ts < fromTs) return false;
      if (toTs && ts > toTs) return false;
      return true;
    }

    const days = Number(viewFilter);
    if (Number.isFinite(days)) {
      const cutoff = startOfToday - (days * 86400000);
      return ts >= cutoff;
    }
    return true;
  }, [getTimestamp, viewFilter, customRange, startOfToday]);

  const recurringSchedules = useMemo(
    () => scheduleData
      .map((item, idx) => ({ item, idx }))
      .filter(({ item }) => item.isRecurring)
      .sort((a, b) => (a.item.dayOfWeek || '').localeCompare(b.item.dayOfWeek || '')),
    [scheduleData],
  );

  const singleSchedules = useMemo(
    () => scheduleData
      .map((item, idx) => ({ item, idx }))
      .filter(({ item }) => !item.isRecurring)
      .filter(({ item }) => matchesFilter(item))
      .sort((a, b) => {
        const aDate = getTimestamp(a.item.date) ?? 0;
        const bDate = getTimestamp(b.item.date) ?? 0;
        return bDate - aDate;
      }),
    [scheduleData, matchesFilter, getTimestamp],
  );

  const filteredEditEntries = useMemo(
    () => scheduleData
      .map((item, idx) => ({ item, idx }))
      .filter(({ item }) => matchesFilter(item))
      .filter(({ item }) => !isEditorialOnly(item)),
    [scheduleData, matchesFilter, isEditorialOnly],
  );

  const visibleEditEntries = chipFilterIdx !== null
    ? filteredEditEntries.filter(entry => entry.idx === chipFilterIdx)
    : filteredEditEntries;

  useEffect(() => {
    if (chipFilterIdx !== null && !filteredEditEntries.some(entry => entry.idx === chipFilterIdx)) {
      setChipFilterIdx(null);
    }
  }, [chipFilterIdx, filteredEditEntries]);

  useEffect(() => {
    if (chipFilterIdx !== null) {
      const exists = filteredEditEntries.some(entry => entry.idx === chipFilterIdx);
      if (!exists) {
        setChipFilterIdx(null);
        return;
      }
      setExpandedIdx(chipFilterIdx);
    }
  }, [chipFilterIdx, filteredEditEntries]);

  useEffect(() => {
    if (
      expandedIdx !== null &&
      !visibleEditEntries.some(entry => entry.idx === expandedIdx)
    ) {
      setExpandedIdx(null);
    }
  }, [visibleEditEntries, expandedIdx]);

  const handleCustomRangeChange = (field, value) => {
    setCustomRange((prev) => ({ ...prev, [field]: value }));
  };

  const handleJumpDateChange = (value) => {
    setJumpDate(value);
    if (!value) return;
    const idx = scheduleData.findIndex(
      (item) => !item.isRecurring && item.date && toYMD(item.date) === toYMD(value),
    );
    if (idx >= 0) {
      setViewFilter('all');
      setChipFilterIdx(idx);
      setExpandedIdx(idx);
      setExpandedViewId(`single-${scheduleData[idx].id || idx}`);
    } else {
      toast.info('Nessuno scheduling trovato per la data selezionata.', toastOptions);
    }
  };

  /* mutations ------------------------------------------------------- */
  const mutateScheduleItem = (idx, updater) =>
    setScheduleData(list => list.map((item, i) => (i === idx ? updater({ ...item }) : item)));

  const addScheduleItem = () => {
    setScheduleData(list => {
      const newList = [
        ...list,
        {
          date: '',
          startTime: '',
          endTime: '',
          programId: program.id,
          guests: [],
          topics: [],
          isRecurring: false,
          dayOfWeek: '',
          description: '',
          priority: 0,
          blacklistDates: [],
          timeSlots: [],
          occurrences: [],
        },
      ];
      // Espandi automaticamente il nuovo item
      setTimeout(() => setExpandedIdx(newList.length - 1), 0);
      return newList;
    });
  };

  const addScheduleForDay = (isoDate) => {
    if (!isoDate) return;

    let nextExpandedIdx = null;
    let nextOccurrenceIdx = null;

    setScheduleData((prev) => {
      const draft = [...prev];

      const matchRecurringIdx = draft.findIndex(
        (item) => item && item.isRecurring && isDateAllowedForSchedule(item, isoDate),
      );
      const firstRecurringIdx = matchRecurringIdx >= 0
        ? matchRecurringIdx
        : draft.findIndex((item) => item && item.isRecurring);

      if (matchRecurringIdx >= 0) {
        const sched = draft[matchRecurringIdx];
        if (sched) {
          const occs = Array.isArray(sched.occurrences) ? [...sched.occurrences] : [];
          const existingIdx = occs.findIndex(o => toYMD(o.date) === isoDate);
          if (existingIdx >= 0) {
            nextExpandedIdx = matchRecurringIdx;
            nextOccurrenceIdx = existingIdx;
            return draft;
          }
          occs.push({
            date: isoDate,
            startTime: sched.startTime || '',
            endTime: sched.endTime || '',
            note: '',
            timeSlots: cloneSlots(sched.timeSlots || [], { keepId: false }),
          });
          draft[matchRecurringIdx] = { ...sched, occurrences: occs };
          nextExpandedIdx = matchRecurringIdx;
          nextOccurrenceIdx = occs.length - 1;
        }
        return draft;
      }

      const cloneTemplate = firstRecurringIdx >= 0 ? draft[firstRecurringIdx] : null;
      const newIdx = draft.filter(Boolean).length;
      draft.push({
        date: isoDate,
        startTime: cloneTemplate?.startTime || '',
        endTime: cloneTemplate?.endTime || '',
        programId: program.id,
        guests: toIdArray(cloneTemplate?.guests) || [],
        topics: toIdArray(cloneTemplate?.topics) || [],
        isRecurring: false,
        dayOfWeek: '',
        description: cloneTemplate?.description || '',
        priority: cloneTemplate?.priority ?? 0,
        blacklistDates: [],
        timeSlots: cloneSlots(cloneTemplate?.timeSlots || [], { keepId: false }),
        occurrences: [],
      });
      nextExpandedIdx = newIdx;
      return draft;
    });

    if (nextExpandedIdx !== null) {
      setExpandedIdx(nextExpandedIdx);
      setSelectedDay(isoDate);
      if (nextOccurrenceIdx !== null) {
        setExpandedViewId(`cal-${nextExpandedIdx}-${nextOccurrenceIdx}`);
      } else {
        setExpandedViewId(`cal-new-${nextExpandedIdx}`);
      }
    }
  };

  const mutateSlots = (idx, scope, occurrenceIndex, mutator) => {
    mutateScheduleItem(idx, (item) => {
      if (scope === 'occurrence' && occurrenceIndex !== null && occurrenceIndex !== undefined) {
        return {
          ...item,
          occurrences: item.occurrences.map((occ, oIdx) => (oIdx === occurrenceIndex
            ? { ...occ, timeSlots: mutator([...(occ.timeSlots || [])]) }
            : occ)),
        };
      }
      return {
        ...item,
        timeSlots: mutator([...(item.timeSlots || [])]),
      };
    });
  };

  const mutateCompanyAssignments = (scheduleIdx, slotIdx, scope, occurrenceIndex, mutator) => {
    mutateSlots(scheduleIdx, scope, occurrenceIndex, (slots) =>
      slots.map((slot, sIdx) => {
        if (sIdx !== slotIdx) return slot;
        const nextAssignments = mutator(Array.isArray(slot.companyAssignments)
          ? [...slot.companyAssignments]
          : []);
        return { ...slot, companyAssignments: nextAssignments };
      }));
  };

  const addTimeSlot = (idx, { scope = 'default', occurrenceIndex = null } = {}) => {
    mutateSlots(idx, scope, occurrenceIndex, (slots) => [...slots, {
      id: null,
      startTime: '',
      endTime: '',
      note: '',
      isEditorial: false,
      people: [],
      topics: [],
      companyAssignments: [],
    }]);
  };

  const updateSlotField = (
    idx,
    slotIdx,
    field,
    value,
    { scope = 'default', occurrenceIndex = null } = {},
  ) => {
    mutateSlots(idx, scope, occurrenceIndex, (slots) =>
      slots.map((slot, sIdx) => {
        if (sIdx !== slotIdx) return slot;
        const next = { ...slot, [field]: value };
        if (field === 'isEditorial' && !value) {
          next.companyAssignments = [];
        }
        return next;
      }));
  };

  const handleDeleteSchedule = async (scheduleId, idx = null) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo scheduling?')) return;
    try {
      if (scheduleId) await api.delete(`/schedules/${scheduleId}`);
      if (idx !== null) setScheduleData(list => list.filter((_, i) => i !== idx));
      refreshProgramsList?.();
    } catch (err) {
      console.error('Errore nellʼeliminazione dello scheduling:', err);
    }
  };

  const addOccurrence = (idx, dateOverride) => {
    // Clone protagonists from program when creating new occurrence
    const programProtagonists = toIdArray(program?.protagonists || []);
    mutateScheduleItem(idx, (item) => ({
      ...item,
      occurrences: [
        ...(item.occurrences || []),
        {
          date: dateOverride || '',
          startTime: item.startTime || '',
          endTime: item.endTime || '',
          note: '',
          timeSlots: cloneSlots(item.timeSlots || [], { keepId: false }),
          protagonists: programProtagonists,
        },
      ],
    }));
  };

  const updateOccurrenceField = (idx, occIdx, field, value) => {
    mutateScheduleItem(idx, (item) => {
      const occurrences = item.occurrences || [];
      if (!occurrences.length) return item;

      let nextValue = value;
      if (field === 'date' && value) {
        const isoValue = toYMD(value);
        if (!isDateAllowedForSchedule(item, isoValue)) {
          emitFormError(
            item.dayOfWeek
              ? `Le occorrenze per questo ricorrente devono cadere di ${item.dayOfWeek}.`
              : 'La data selezionata non è valida per questa ricorrenza.',
          );
          return item;
        }
        nextValue = isoValue;
      }

      emitFormError('');
      return {
        ...item,
        occurrences: occurrences.map((occ, oIdx) =>
          (oIdx === occIdx ? { ...occ, [field]: nextValue } : occ)),
      };
    });
  };

  const removeOccurrence = (idx, occIdx) => {
    mutateScheduleItem(idx, (item) => ({
      ...item,
      occurrences: item.occurrences.filter((_, oIdx) => oIdx !== occIdx),
    }));
  };

  const validateSchedulePayload = (list = []) => {
    for (const schedule of list) {
      const scheduleLabel = describeScheduleLabel(schedule);
      if (!isTimeRangeValid(schedule?.startTime, schedule?.endTime)) {
        return `${scheduleLabel} deve avere un intervallo orario valido.`;
      }

      const invalidSlot = (schedule.timeSlots || []).find(
        slot => slot.startTime && slot.endTime && !isTimeRangeValid(slot.startTime, slot.endTime),
      );
      if (invalidSlot) {
        return `Le fasce orarie di ${scheduleLabel} devono avere un intervallo orario valido.`;
      }

      const outOfBoundsSlot = (schedule.timeSlots || []).find(
        slot => slot.startTime && slot.endTime &&
          !slotFitsWithinParent(slot.startTime, slot.endTime, schedule.startTime, schedule.endTime),
      );
      if (outOfBoundsSlot) {
        return `Le fasce orarie di ${scheduleLabel} devono rientrare tra ${schedule.startTime} e ${schedule.endTime}.`;
      }

      for (const occurrence of schedule.occurrences || []) {
        const occLabel = describeOccurrenceLabel(occurrence, schedule);
        if (occurrence.date) {
          const isoDate = toYMD(occurrence.date);
          if (isoDate && !isDateAllowedForSchedule(schedule, isoDate)) {
            return schedule.dayOfWeek
              ? `Le occorrenze devono cadere di ${schedule.dayOfWeek}.`
              : 'La data selezionata per un\'occorrenza non è valida.';
          }
        }

        if (!isTimeRangeValid(occurrence.startTime, occurrence.endTime)) {
          return `${occLabel} deve avere un intervallo orario valido.`;
        }

        const invalidOccurrenceSlot = (occurrence.timeSlots || []).find(
          slot => slot.startTime && slot.endTime && !isTimeRangeValid(slot.startTime, slot.endTime),
        );
        if (invalidOccurrenceSlot) {
          return `Le fasce orarie di ${occLabel} devono avere un intervallo orario valido.`;
        }

        const outOfBoundsOccurrenceSlot = (occurrence.timeSlots || []).find(
          slot => slot.startTime && slot.endTime &&
            !slotFitsWithinParent(slot.startTime, slot.endTime, occurrence.startTime, occurrence.endTime),
        );
        if (outOfBoundsOccurrenceSlot) {
          return `Le fasce orarie di ${occLabel} devono rientrare tra ${occurrence.startTime} e ${occurrence.endTime}.`;
        }
      }
    }
    return null;
  };

  const serializeAssignments = (assignments = []) => (
    Array.isArray(assignments)
      ? assignments
          .map((assign) => {
            const companyId = extractNumericId(
              assign.companyId ?? assign.company_id ?? assign.company,
            );
            const personIds = toIdArray(assign.personIds ?? assign.person_ids ?? assign.people);
            if (!companyId || !personIds.length) return null;
            return { companyId, personIds };
          })
          .filter(Boolean)
      : []
  );

  const serializeSlot = (slot) => ({
    ...(slot.id ? { id: slot.id } : {}),
    startTime: slot.startTime,
    endTime: slot.endTime,
    note: slot.note || '',
    isEditorial: !!slot.isEditorial,
    people: toIdArray(slot.people),
    topics: toIdArray(slot.topics),
    companyAssignments: serializeAssignments(slot.companyAssignments),
    rubricaId: slot.rubricaId || null,
    tematiche: slot.tematiche || '',
  });

  const serializeOccurrences = (occurrences = []) =>
    (Array.isArray(occurrences) ? occurrences : [])
      .map((occ) => {
        const occDate = toYMD(occ.date);
        if (!occDate || !occ.startTime || !occ.endTime) return null;
        return {
          ...(occ.id ? { id: occ.id } : {}),
          date: occDate,
          startTime: occ.startTime,
          endTime: occ.endTime,
          note: occ.note || '',
          isOverride: typeof occ.isOverride === 'boolean' ? occ.isOverride : true,
          status: occ.status || 'scheduled',
          timeSlots: (occ.timeSlots || [])
            .filter(slot => slot.startTime && slot.endTime)
            .map(serializeSlot),
          protagonists: toIdArray(occ.protagonists),
        };
      })
      .filter(Boolean);

  const serializeSchedule = (it) => {
      const normalizedDate = it.isRecurring ? undefined : toYMD(it.date);
      const blacklist = it.isRecurring
        ? Array.from(
            new Set(
              (it.blacklistDates || [])
                .map(d =>
                  typeof d === 'string' ? toYMD(d) : d?.format?.('YYYY-MM-DD') ?? '',
                )
                .filter(Boolean),
            ),
          )
        : [];

      return {
        ...(it.id ? { id: it.id } : {}),
        programId: program.id,
        isRecurring: !!it.isRecurring,
        dayOfWeek: it.isRecurring ? it.dayOfWeek : '',
        date: it.isRecurring ? undefined : (normalizedDate || undefined),
        startTime: it.startTime,
        endTime: it.endTime,
        description: it.description || '',
        priority: Number.isFinite(Number(it.priority)) ? Number(it.priority) : 0,
        guests: toIdArray(it.guests),
        topics: toIdArray(it.topics),
        blacklistDates: blacklist,
        timeSlots: (it.timeSlots || [])
          .filter(slot => slot.startTime && slot.endTime)
          .map(serializeSlot),
        occurrences: serializeOccurrences(it.occurrences),
      };
  };

  const handleSaveSchedule = async () => {
    if (savingRef.current) return;

    const validationError = validateSchedulePayload(scheduleData);
    if (validationError) {
      emitFormError(validationError);
      return;
    }

    const ready = scheduleData.map(serializeSchedule);
    const initial = initialScheduleDataRef.current.map(serializeSchedule);
    const changedSchedules = selectChangedSchedules(ready, initial);

    savingRef.current = true;
    setIsSaving(true);
    emitFormError('');

    try {
      await onScheduleUpdate(program.id, changedSchedules);
      emitFormError('');
      setIsEditing(false);
    } catch (error) {
      const apiMessage = error?.code === 'ECONNABORTED'
        ? 'Il salvataggio ha superato il tempo massimo. Le modifiche non sono state salvate: verifica la connessione e riprova.'
        : !error?.response
          ? 'Connessione interrotta durante il salvataggio. Le modifiche non sono state salvate: riprova senza chiudere la finestra.'
          : error.response.data?.error || 'Errore nel salvataggio dello scheduling';
      emitFormError(apiMessage);
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  const handleRequestClose = () => {
    if (savingRef.current) {
      toast.info('Salvataggio in corso: attendi la conferma prima di chiudere.', toastOptions);
      return;
    }
    onClose();
  };

  /* render helpers -------------------------------------------------- */
  const renderFilterControls = (context) => (
    <div className={`view-toolbar ${context}`}>
      <div className="view-filter">
        <label htmlFor={`viewRange-${context}`}>Mostra eventi:</label>
        <select
          id={`viewRange-${context}`}
          value={viewFilter}
          onChange={e => setViewFilter(e.target.value)}
        >
          {viewFilterOptions.map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {viewFilter === 'custom' && (
        <div className="view-custom-range">
          <label>
            Dal
            <input
              type="date"
              value={customRange.from}
              onChange={e => handleCustomRangeChange('from', e.target.value)}
            />
          </label>
          <label>
            Al
            <input
              type="date"
              value={customRange.to}
              onChange={e => handleCustomRangeChange('to', e.target.value)}
            />
          </label>
        </div>
      )}

      <div className="view-jump">
        <label>
          Vai alla data:
          <input
            type="date"
            value={jumpDate}
            onChange={e => handleJumpDateChange(e.target.value)}
          />
        </label>
      </div>

    </div>
  );

  const renderSlotEditors = (slots = [], scheduleIdx, scope = 'default', occurrenceIndex = null) => {
    const schedule = scheduleData[scheduleIdx];
    const occurrence = scope === 'occurrence' && typeof occurrenceIndex === 'number'
      ? schedule?.occurrences?.[occurrenceIndex]
      : null;
    const parentStart = scope === 'occurrence' ? occurrence?.startTime : schedule?.startTime;
    const parentEnd = scope === 'occurrence' ? occurrence?.endTime : schedule?.endTime;

    return (
      <div className="time-slot-edit-list">
        {slots.some(slot => !slot.isEditorial) ? (
          slots
            .map((slot, slotIdx) => ({ slot, slotIdx }))
            .filter(({ slot }) => !slot.isEditorial)
            .map(({ slot, slotIdx }) => (
            <div
              key={`${scope}-${scheduleIdx}-${occurrenceIndex ?? 'root'}-${slot.id || slotIdx}`}
              className="time-slot-card"
            >
              <div className="time-slot-card-header">
                <strong>Fascia {slotIdx + 1}</strong>
                <button
                  type="button"
                  className="remove-chip"
                  onClick={() => mutateSlots(scheduleIdx, scope, occurrenceIndex, (list) => list.filter((_, i) => i !== slotIdx))}
                >
                  ×
                </button>
              </div>
              <div className="time-slot-grid">
                <label>
                  Ora Inizio:
                  <input
                    type="time"
                    value={slot.startTime}
                    min={parentStart || undefined}
                    max={parentEnd || undefined}
                    onChange={e => updateSlotField(scheduleIdx, slotIdx, 'startTime', e.target.value, { scope, occurrenceIndex })}
                  />
                </label>
                <label>
                  Ora Fine:
                  <input
                    type="time"
                    value={slot.endTime}
                    min={parentStart || undefined}
                    max={parentEnd || undefined}
                    onChange={e => updateSlotField(scheduleIdx, slotIdx, 'endTime', e.target.value, { scope, occurrenceIndex })}
                  />
                </label>
                <label>
                  Nota:
                  <input
                    type="text"
                    value={slot.note}
                    onChange={e => updateSlotField(scheduleIdx, slotIdx, 'note', e.target.value, { scope, occurrenceIndex })}
                    placeholder="Dettagli fascia"
                  />
                </label>
              </div>

              <div className="time-slot-section">
                <h5>Ospiti della fascia</h5>
                <Select
                  className="react-select"
                  classNamePrefix="select"
                  isMulti
                  options={peopleOptions}
                  filterOption={personOptionMatches}
                  noOptionsMessage={noPeopleOptionsMessage}
                  value={(slot.people || [])
                    .map(pid => peopleOptionById.get(pid))
                    .filter(Boolean)}
                  placeholder="Cerca o aggiungi ospiti..."
                  onChange={(selected) => {
                    const nextPeople = Array.isArray(selected)
                      ? selected.map(opt => opt.value)
                      : [];
                    updateSlotField(
                      scheduleIdx,
                      slotIdx,
                      'people',
                      nextPeople,
                      { scope, occurrenceIndex },
                    );
                  }}
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                />
              </div>

              <div className="time-slot-section">
                <h5>Topics</h5>
                <Select
                  className="react-select"
                  classNamePrefix="select"
                  isMulti
                  options={topicOptions}
                  value={(slot.topics || [])
                    .map(tid => topicOptionById.get(tid))
                    .filter(Boolean)}
                  placeholder="Seleziona topic..."
                  onChange={(selected) => {
                    const nextTopics = Array.isArray(selected)
                      ? selected.map(opt => opt.value)
                      : [];
                    updateSlotField(
                      scheduleIdx,
                      slotIdx,
                      'topics',
                      nextTopics,
                      { scope, occurrenceIndex },
                    );
                  }}
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                />
              </div>

              <div className="time-slot-section">
                <h5>Rubrica</h5>
                <Select
                  className="react-select"
                  classNamePrefix="select"
                  isClearable
                  options={rubricaOptions}
                  value={slot.rubricaId ? rubricaOptionById.get(slot.rubricaId) : null}
                  placeholder="Seleziona rubrica..."
                  onChange={(selected) => {
                    updateSlotField(
                      scheduleIdx,
                      slotIdx,
                      'rubricaId',
                      selected ? selected.value : null,
                      { scope, occurrenceIndex },
                    );
                  }}
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                />
              </div>

              <div className="time-slot-section">
                <h5>Tematiche</h5>
                <input
                  type="text"
                  value={slot.tematiche || ''}
                  onChange={e => updateSlotField(scheduleIdx, slotIdx, 'tematiche', e.target.value, { scope, occurrenceIndex })}
                  placeholder="Tematiche della fascia..."
                />
              </div>

              {slot.isEditorial && (
                <div className="time-slot-section company-assignment-section">
                  <h5>Collegamenti azienda</h5>
                  {(slot.companyAssignments || []).length ? (
                    (slot.companyAssignments || []).map((assignment, assignIdx) => {
                      const company = companyById.get(assignment.companyId);
                      const assignedPeople = Array.isArray(assignment.personIds) ? assignment.personIds : [];
                      return (
                        <div key={`assign-${assignIdx}`} className="company-assignment-card">
                          <div className="company-assignment-header">
                            <Select
                              className="react-select"
                              classNamePrefix="select"
                              isClearable
                              options={companyOptions}
                              value={companyOptions.find(opt => opt.value === assignment.companyId) || null}
                              placeholder="Seleziona azienda..."
                              onChange={(selected) => {
                                const nextCompany = selected ? selected.value : null;
                                mutateCompanyAssignments(
                                  scheduleIdx,
                                  slotIdx,
                                  scope,
                                  occurrenceIndex,
                                  (list) => list.map((entry, idx) =>
                                    idx === assignIdx
                                      ? { ...entry, companyId: nextCompany, personIds: [] }
                                      : entry),
                                );
                              }}
                              menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                            />
                            <button
                              type="button"
                              className="remove-chip"
                              onClick={() => mutateCompanyAssignments(
                                scheduleIdx,
                                slotIdx,
                                scope,
                                occurrenceIndex,
                                (list) => list.filter((_, idx) => idx !== assignIdx),
                              )}
                            >
                              ×
                            </button>
                          </div>

                          {assignment.companyId && (
                            <Select
                              className="react-select"
                              classNamePrefix="select"
                              isMulti
                              options={peopleOptions}
                              filterOption={personOptionMatches}
                              noOptionsMessage={noPeopleOptionsMessage}
                              value={assignedPeople
                                .map(pid => peopleOptionById.get(pid))
                                .filter(Boolean)}
                              placeholder={company ? `Persone per ${company.name}` : 'Associa persona...'}
                              onChange={(selected) => {
                                const nextPeople = Array.isArray(selected)
                                  ? selected.map(opt => opt.value)
                                  : [];
                                mutateCompanyAssignments(
                                  scheduleIdx,
                                  slotIdx,
                                  scope,
                                  occurrenceIndex,
                                  (list) => list.map((entry, idx) =>
                                    idx === assignIdx
                                      ? { ...entry, personIds: nextPeople }
                                      : entry),
                                );
                              }}
                              menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                            />
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <p className="time-slot-empty">Nessuna azienda associata.</p>
                  )}
                  <button
                    type="button"
                    className="add-entry-btn"
                    onClick={() => mutateCompanyAssignments(
                      scheduleIdx,
                      slotIdx,
                      scope,
                      occurrenceIndex,
                      (list) => [...list, { companyId: null, personIds: [] }],
                    )}
                  >
                    + Collega azienda
                  </button>
                </div>
              )}
            </div>
          ))
        ) : (
          <p className="time-slot-empty">Nessuna fascia definita.</p>
        )}
        <button
          type="button"
          className="add-entry-btn"
          onClick={() => addTimeSlot(scheduleIdx, { scope, occurrenceIndex })}
        >
          Aggiungi Fascia Oraria
        </button>
      </div>
    );
  };

  const renderSlotView = (slots = []) => {
    const viewSlots = (slots || []).filter(slot => !slot.isEditorial);
    if (!viewSlots.length) return null;
    return (
      <div className="time-slot-view-list">
        {viewSlots.map((slot, idx) => (
          <div key={`slot-view-${idx}-${slot.id || 'new'}`} className="time-slot-view-card">
            <div className="time-slot-view-header">
              <strong>
                {slot.startTime?.slice(0, 5)} - {slot.endTime?.slice(0, 5)}
              </strong>
            </div>
            {slot.note && <p className="time-slot-note">{slot.note}</p>}

            {Array.isArray(slot.people) && slot.people.length > 0 && (
              <>
                <h6>Ospiti</h6>
                <div className="chip-container">
                  {slot.people.map(pid => {
                    const person = typeof pid === 'object' ? pid : peopleById.get(pid);
                    if (!person) return null;
                    return (
                      <div
                        key={person.id}
                        className="chip"
                        onClick={() => handlePersonClick(person)}
                        style={{ cursor: 'pointer' }}
                      >
                        <img src={avatarFor(person)} alt="" />
                        <span>{person.nome} {person.cognome}</span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {Array.isArray(slot.topics) && slot.topics.length > 0 && (
              <>
                <h6>Topics</h6>
                <div className="chip-container">
                  {slot.topics.map(topicId => {
                    const topic = typeof topicId === 'object' ? topicId : topicById.get(topicId);
                    if (!topic) return null;
                    return (
                      <div key={topic.id} className="chip topic-chip">
                        <span>{topic.name}</span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {slot.rubricaId && rubricaOptionById.get(slot.rubricaId) && (
              <>
                <h6>Rubrica</h6>
                <p className="time-slot-note">{rubricaOptionById.get(slot.rubricaId).label}</p>
              </>
            )}

            {slot.tematiche && (
              <>
                <h6>Tematiche</h6>
                <p className="time-slot-note">{slot.tematiche}</p>
              </>
            )}

            {Array.isArray(slot.companyAssignments) && slot.companyAssignments.length > 0 && (
              <>
                <h6>Aziende collegate</h6>
                <div className="company-assignment-view">
                  {slot.companyAssignments.map((assignment, idxAssign) => {
                    const company = companyById.get(assignment.companyId);
                    if (!company) return null;
                    return (
                      <div key={`view-company-${idxAssign}`} className="company-assignment-view-item">
                        <div className="company-assignment-view-head">
                          <img src={companyLogoFor(company)} alt={company.name} />
                          <strong>{company.name}</strong>
                        </div>
                        {Array.isArray(assignment.personIds) && assignment.personIds.length > 0 && (
                          <div className="chip-container">
                            {assignment.personIds.map(pid => {
                              const person = peopleById.get(pid);
                              if (!person) return null;
                              return (
                                <div
                                  key={`${pid}-${idxAssign}`}
                                  className="chip"
                                  onClick={() => handlePersonClick(person)}
                                  style={{ cursor: 'pointer' }}
                                >
                                  <img src={avatarFor(person)} alt="" />
                                  <span>{person.nome} {person.cognome}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderViewDetails = (item) => (
    <div className="schedule-view-details">
      {item.blacklistDates?.length > 0 && (
        <>
          <h5 className="schedule-chips-title">Date escluse</h5>
          <div className="schedule-chips-container">
            {item.blacklistDates.map(d => (
              <div key={d} className="chip">
                <span>{formatDisplayDate(d)}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {item.description && (
        <>
          <h5 className="schedule-chips-title">Descrizione</h5>
          <p className="schedule-view-text">{item.description}</p>
        </>
      )}

      {item.guests?.length > 0 && (
        <>
          <h5 className="schedule-chips-title">Ospiti</h5>
          <div className="schedule-chips-container">
            {item.guests.map(id => {
              const person = peopleById.get(id);
              if (!person) return null;
              return (
                <div
                  key={person.id}
                  className="chip"
                  onClick={() => handlePersonClick(person)}
                  style={{ cursor: 'pointer' }}
                >
                  <img src={avatarFor(person)} alt="" />
                  <span>{person.nome} {person.cognome}</span>
                </div>
              );
            })}
          </div>
        </>
      )}

      {item.topics?.length > 0 && (
        <>
          <h5 className="schedule-chips-title">Argomenti</h5>
          <div className="schedule-chips-container">
            {item.topics.map(id => {
              const topic = topicById.get(id);
              if (!topic) return null;
              return (
                <div key={topic.id} className="chip topic-chip">
                  <span>{topic.name}</span>
                </div>
              );
            })}
          </div>
        </>
      )}

      {program?.protagonists?.length > 0 && (
        <>
          <h5 className="schedule-chips-title">Protagonisti</h5>
          <div className="schedule-chips-container">
            {program.protagonists.map(p => (
              <div
                key={p.id}
                className="chip"
                onClick={() => handlePersonClick(p)}
                style={{ cursor: 'pointer' }}
              >
                <img src={p.imageUrl || p.image_url || 'https://via.placeholder.com/26'} alt="" />
                <span>{p.nome} {p.cognome}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {item.timeSlots?.length > 0 && (
        <>
          <h5 className="schedule-chips-title">Fasce orarie</h5>
          {renderSlotView(item.timeSlots)}
        </>
      )}

      {item.occurrences?.length > 0 && (
        <>
          <h5 className="schedule-chips-title">Occorrenze personalizzate</h5>
          <div className="occurrence-view-list">
            {item.occurrences.map(occ => (
              <div key={occ.id || occ.date} className="occurrence-view-card">
                <strong>{formatDisplayDate(occ.date)}</strong>
                <span className="summary-time">{occ.startTime} - {occ.endTime}</span>
                {renderSlotView(occ.timeSlots || [])}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );

  const toggleViewCard = (id) => {
    setExpandedViewId((prev) => (prev === id ? null : id));
  };

  const renderViewCard = ({ item, idx }, prefix) => {
    const key = `${prefix}-${item.id || idx}`;
    const isOpen = expandedViewId === key;
    const dateLabel = item.isRecurring
      ? `Ricorrente: ${item.dayOfWeek || 'N/D'}`
      : formatDisplayDate(item.date) || 'Data non impostata';
    const summaryText = item.description
      ? item.description.slice(0, 80)
      : 'Nessuna descrizione';

    return (
      <div key={key} className={`schedule-view-card ${isOpen ? 'open' : ''}`}>
        <div className="schedule-view-summary">
          <div className="summary-content" onClick={() => toggleViewCard(key)}>
            <span className="badge-priority">P{item.priority ?? 0}</span>
            {item.isRecurring && <span className="badge-recurring">Ricorrente</span>}
            <div className="summary-main-line">
              <strong>{dateLabel}</strong>
              <span className="summary-time">{item.startTime} - {item.endTime}</span>
            </div>
            <span className="summary-desc">{summaryText}</span>
          </div>
          <div className="summary-actions">
            <button
              type="button"
              className="summary-toggle"
              onClick={() => toggleViewCard(key)}
            >
              {isOpen ? 'Nascondi' : 'Dettagli'}
            </button>
            <button
              type="button"
              className="delete-schedule-btn view"
              onClick={() => handleDeleteSchedule(item.id, idx)}
              title="Elimina scheduling"
            >
              ×
            </button>
          </div>
        </div>
        {isOpen && renderViewDetails(item)}
      </div>
    );
  };

  const visibleViewRecurring = recurringSchedules;
  const visibleViewSingles = singleSchedules;
  const visibleRecurringNonEditorial = visibleViewRecurring.filter(({ item }) => !isEditorialOnly(item));
  const visibleSinglesNonEditorial = visibleViewSingles.filter(({ item }) => !isEditorialOnly(item));
  const editMapEntries = useMemo(
    () => scheduleData.map((item, idx) => ({ item, idx })).filter(({ item }) => !isEditorialOnly(item)),
    [scheduleData, isEditorialOnly],
  );

  const viewDayEntries = useMemo(() => {
    if (!viewDay) return [];
    const iso = toYMD(viewDay);
    return scheduleData.flatMap((item, idx) => {
      if (!item) return [];
      if (isEditorialOnly(item)) return [];
      if (item.isRecurring) {
        if (!isDateAllowedForSchedule(item, iso)) return [];
        const occ = (item.occurrences || []).find(o => toYMD(o.date) === iso);
        if (occ) return [{ item, idx, occurrence: occ, type: 'occurrence' }];
        return [{ item, idx, occurrence: null, type: 'template' }];
      }
      if (toYMD(item.date) !== iso) return [];
      return [{ item, idx, occurrence: null, type: 'single' }];
    });
  }, [scheduleData, viewDay, isEditorialOnly]);

  const renderViewCalendar = () => (
    <div className="calendar-edit">
      <div className="calendar-toolbar">
        <label>
          Giorno:
          <input
            type="date"
            value={viewDay || ''}
            onChange={e => setViewDay(e.target.value)}
          />
        </label>
      </div>

      {viewDayEntries.length ? (
        viewDayEntries.map(({ item, idx, occurrence, type }) => (
          <div key={`view-cal-${item.id || idx}-${type}-${occurrence?.id || 'base'}`} className="schedule-view-card open">
            <div className="schedule-view-summary">
              <div className="summary-content">
                <div className="summary-main-line">
                  <strong>
                    {item.isRecurring ? `${item.dayOfWeek || 'Ricorrente'} • ${formatDisplayDate(viewDay)}` : formatDisplayDate(item.date)}
                  </strong>
                  <span className="summary-time">
                    {occurrence ? `${occurrence.startTime} - ${occurrence.endTime}` : `${item.startTime} - ${item.endTime}`}
                  </span>
                </div>
                <span className="summary-desc">{item.description ? item.description.slice(0, 80) : 'Nessuna descrizione'}</span>
                {occurrence?.note && (
                  <span className="summary-note"><em>Nota: {occurrence.note}</em></span>
                )}
              </div>
              <div className="summary-actions">
                <span className="badge-priority">P{item.priority ?? 0}</span>
                {item.isRecurring && <span className="badge-recurring">Ricorrente</span>}
              </div>
            </div>
            <div className="schedule-view-details">
              {/* Protagonisti */}
              {program?.protagonists?.length > 0 && (
                <div className="view-protagonists">
                  <label>Protagonisti</label>
                  <div className="schedule-modal-chip-list">
                    {program.protagonists.map(p => (
                      <div key={p.id} className="schedule-modal-chip protagonist-chip">
                        <img
                          src={p.imageUrl || p.image_url || 'https://via.placeholder.com/26'}
                          alt={`${p.nome} ${p.cognome}`}
                        />
                        <span>{p.nome} {p.cognome}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Fasce orarie */}
              {occurrence
                ? renderSlotView(occurrence.timeSlots || [])
                : renderSlotView(item.timeSlots || [])}
            </div>
          </div>
        ))
      ) : (
        <p className="time-slot-empty">Nessuno scheduling per la data selezionata.</p>
      )}
    </div>
  );

  const renderCalendarEdit = () => (
    <div className="calendar-edit">
      <div className="calendar-toolbar">
        <label>
          Giorno:
          <input
            type="date"
            value={selectedDay || ''}
            onChange={e => setSelectedDay(e.target.value)}
          />
        </label>
        <button
          type="button"
          className="add-entry-btn"
          onClick={() => addScheduleForDay(selectedDay)}
        >
          + Nuovo scheduling nel giorno
        </button>
      </div>

      {calendarEntries.length ? (
        calendarEntries.map((entry) => {
          const { schedule, scheduleIdx, occurrenceIdx, type } = entry;
          const isRecurring = schedule.isRecurring;
          const hasOccurrence = type === 'occurrence';

          return (
            <div key={`cal-${type}-${schedule.id || scheduleIdx}-${occurrenceIdx ?? 'base'}`} className="schedule-item-edit-container open">
              <div className="schedule-item-edit-header">
                <div className="schedule-item-edit-summary">
                  <span className="summary-title">
                    {isRecurring
                      ? `${schedule.dayOfWeek || 'Ricorrente'} • ${selectedDay || ''}`
                      : formatDisplayDate(schedule.date)}
                  </span>
                  <span className="summary-time">
                    {type === 'single'
                      ? `${schedule.startTime} - ${schedule.endTime}`
                      : hasOccurrence
                        ? `${schedule.occurrences[occurrenceIdx].startTime} - ${schedule.occurrences[occurrenceIdx].endTime}`
                        : `${schedule.startTime} - ${schedule.endTime}`}
                  </span>
                </div>
                {isRecurring && <span className="badge-recurring">Ricorrente</span>}
              </div>

            <div className="schedule-item-edit-body">
              {type === 'single' && (
                <>
                  <div className="field-grid">
                    <label className="field-block">
                      <span>Priorità</span>
                      <input
                        type="number"
                        min={0}
                        value={schedule.priority ?? 0}
                        onChange={e => mutateScheduleItem(scheduleIdx, curr => ({ ...curr, priority: +e.target.value || 0 }))}
                      />
                    </label>
                  </div>

                  <div className="field-grid">
                    <label className="field-block">
                      <span>Ora inizio</span>
                      <input
                        type="time"
                          value={schedule.startTime || ''}
                          onChange={e => mutateScheduleItem(scheduleIdx, curr => ({ ...curr, startTime: e.target.value }))}
                        />
                      </label>
                      <label className="field-block">
                        <span>Ora fine</span>
                        <input
                          type="time"
                          value={schedule.endTime || ''}
                          onChange={e => mutateScheduleItem(scheduleIdx, curr => ({ ...curr, endTime: e.target.value }))}
                        />
                      </label>
                      <label className="field-block full">
                        <span>Descrizione</span>
                        <textarea
                          value={schedule.description || ''}
                          onChange={e => mutateScheduleItem(scheduleIdx, curr => ({ ...curr, description: e.target.value }))}
                        />
                      </label>
                      </div>
                    <div className="field-stack">
                      <label>Fasce orarie</label>
                      {renderSlotEditors(schedule.timeSlots || [], scheduleIdx)}
                    </div>
                  </>
                )}

                {isRecurring && (
                  <>
                    <div className="field-grid">
                      <label className="field-block">
                        <span>Priorità</span>
                        <input
                          type="number"
                          min={0}
                          value={schedule.priority ?? 0}
                          onChange={e => mutateScheduleItem(scheduleIdx, curr => ({ ...curr, priority: +e.target.value || 0 }))}
                        />
                      </label>
                      </div>

                    {hasOccurrence ? (
                      <>
                        <div className="field-grid">
                          <label className="field-block">
                            <span>Ora inizio occorrenza</span>
                            <input
                              type="time"
                              value={schedule.occurrences[occurrenceIdx].startTime || ''}
                              min={schedule.startTime || undefined}
                              max={schedule.endTime || undefined}
                              onChange={e => updateOccurrenceField(scheduleIdx, occurrenceIdx, 'startTime', e.target.value)}
                            />
                          </label>
                          <label className="field-block">
                            <span>Ora fine occorrenza</span>
                            <input
                              type="time"
                              value={schedule.occurrences[occurrenceIdx].endTime || ''}
                              min={schedule.startTime || undefined}
                              max={schedule.endTime || undefined}
                              onChange={e => updateOccurrenceField(scheduleIdx, occurrenceIdx, 'endTime', e.target.value)}
                            />
                          </label>
                          {/* Protagonisti override per questa occorrenza */}
                          <div className="field-block full occurrence-protagonists-wrapper">
                            <span>Protagonisti giornata</span>
                            <Select
                              className="react-select"
                              classNamePrefix="select"
                              isMulti
                              options={peopleOptions}
                              filterOption={personOptionMatches}
                              noOptionsMessage={() => 'Nessun risultato'}
                              value={(schedule.occurrences[occurrenceIdx].protagonists || [])
                                .map(pid => peopleOptionById.get(pid))
                                .filter(Boolean)}
                              placeholder="Cerca protagonisti..."
                              onChange={(selected) => {
                                const nextProtagonists = Array.isArray(selected)
                                  ? selected.map(opt => opt.value)
                                  : [];
                                updateOccurrenceField(scheduleIdx, occurrenceIdx, 'protagonists', nextProtagonists);
                              }}
                              menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                            />
                            {program?.protagonists?.length > 0 && (
                              <button
                                type="button"
                                className="reset-protagonists-btn"
                                onClick={() => {
                                  const programProtagonists = toIdArray(program.protagonists);
                                  updateOccurrenceField(scheduleIdx, occurrenceIdx, 'protagonists', programProtagonists);
                                }}
                              >
                                Ripristina dal programma
                              </button>
                            )}
                          </div>
                          <label className="field-block full">
                            <span>Nota</span>
                            <input
                              type="text"
                              value={schedule.occurrences[occurrenceIdx].note || ''}
                              onChange={e => updateOccurrenceField(scheduleIdx, occurrenceIdx, 'note', e.target.value)}
                            />
                          </label>
                        </div>
                        <div className="field-stack">
                          <label>Fasce orarie della giornata</label>
                          {renderSlotEditors(schedule.occurrences[occurrenceIdx].timeSlots || [], scheduleIdx, 'occurrence', occurrenceIdx)}
                        </div>
                      </>
                    ) : (
                      <>
                        <p className="template-hint">
                          Stai visualizzando il template ricorrente. Modifica per creare un'occorrenza personalizzata per {selectedDay}.
                        </p>
                        <div className="field-grid">
                          <label className="field-block">
                            <span>Ora inizio</span>
                            <input
                              type="time"
                              value={schedule.startTime || ''}
                              onChange={e => ensureOccurrenceAndUpdateField(scheduleIdx, selectedDay, 'startTime', e.target.value)}
                            />
                          </label>
                          <label className="field-block">
                            <span>Ora fine</span>
                            <input
                              type="time"
                              value={schedule.endTime || ''}
                              onChange={e => ensureOccurrenceAndUpdateField(scheduleIdx, selectedDay, 'endTime', e.target.value)}
                            />
                          </label>
                          <label className="field-block full">
                            <span>Nota</span>
                            <input
                              type="text"
                              placeholder="Aggiungi nota per questa data..."
                              onChange={e => ensureOccurrenceAndUpdateField(scheduleIdx, selectedDay, 'note', e.target.value)}
                            />
                          </label>
                        </div>
                        {/* Protagonisti dal programma (read-only) */}
                        {program?.protagonists?.length > 0 && (
                          <div className="field-stack">
                            <label>Protagonisti</label>
                            <div className="schedule-modal-chip-list">
                              {program.protagonists.map(p => (
                                <div key={p.id} className="schedule-modal-chip protagonist-chip">
                                  <img
                                    src={p.imageUrl || p.image_url || 'https://via.placeholder.com/26'}
                                    alt={`${p.nome} ${p.cognome}`}
                                  />
                                  <span>{p.nome} {p.cognome}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        <div className="field-stack">
                          <label>Fasce orarie (template)</label>
                          <p className="template-slots-hint">
                            Per modificare le fasce orarie di questa data, clicca su "Personalizza giornata".
                          </p>
                          {renderSlotView(schedule.timeSlots || [])}
                          <button
                            type="button"
                            className="add-entry-btn"
                            onClick={() => {
                              const idx = ensureOccurrenceForDate(scheduleIdx, selectedDay);
                              setExpandedIdx(scheduleIdx);
                              setExpandedViewId(`cal-${scheduleIdx}-${idx}`);
                            }}
                          >
                            Personalizza giornata
                          </button>
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })
      ) : (
        <p className="time-slot-empty">Nessuno scheduling per la data selezionata.</p>
      )}
    </div>
  );

  const findOccurrenceIndexForDate = useCallback((schedule, isoDate) => {
    if (!isoDate || !Array.isArray(schedule?.occurrences)) return -1;
    return schedule.occurrences.findIndex(occ => toYMD(occ.date) === toYMD(isoDate));
  }, []);

  const calendarEntries = useMemo(() => {
    if (!selectedDay) return [];
    const iso = toYMD(selectedDay);
    return scheduleData.flatMap((item, idx) => {
      if (!item) return [];
      if (!iso) return [];
      if (item.isRecurring) {
        if (!isDateAllowedForSchedule(item, iso)) return [];
        const occIdx = findOccurrenceIndexForDate(item, iso);
        if (occIdx >= 0) {
          return [{ schedule: item, scheduleIdx: idx, occurrenceIdx: occIdx, type: 'occurrence' }];
        }
        return [{ schedule: item, scheduleIdx: idx, occurrenceIdx: null, type: 'template' }];
      }
      if (toYMD(item.date) !== iso) return [];
      return [{ schedule: item, scheduleIdx: idx, occurrenceIdx: null, type: 'single' }];
    });
  }, [scheduleData, selectedDay, findOccurrenceIndexForDate]);

  const ensureOccurrenceForDate = (scheduleIdx, isoDate) => {
    const sched = scheduleData[scheduleIdx];
    const existingIdx = findOccurrenceIndexForDate(sched, isoDate);
    if (existingIdx >= 0) return existingIdx;

    let newIdx = 0;
    mutateScheduleItem(scheduleIdx, (item) => {
      const occs = item.occurrences ? [...item.occurrences] : [];
      newIdx = occs.length;
      occs.push({
        date: isoDate,
        startTime: item.startTime || '',
        endTime: item.endTime || '',
        note: '',
        timeSlots: cloneSlots(item.timeSlots || [], { keepId: false }),
        protagonists: toIdArray(program?.protagonists || []),
      });
      return { ...item, occurrences: occs };
    });
    return newIdx;
  };

  // Funzione per creare occorrenza E aggiornare un campo in un'unica operazione atomica
  const ensureOccurrenceAndUpdateField = (scheduleIdx, isoDate, field, value) => {
    setScheduleData(list => list.map((item, i) => {
      if (i !== scheduleIdx) return item;

      const occs = item.occurrences ? [...item.occurrences] : [];
      let occIdx = occs.findIndex(occ => toYMD(occ.date) === toYMD(isoDate));

      // Se non esiste, crea nuova occorrenza con protagonisti dal program
      if (occIdx < 0) {
        occIdx = occs.length;
        occs.push({
          date: isoDate,
          startTime: item.startTime || '',
          endTime: item.endTime || '',
          note: '',
          timeSlots: cloneSlots(item.timeSlots || [], { keepId: false }),
          protagonists: toIdArray(program?.protagonists || []),
        });
      }

      // Aggiorna il campo nell'occorrenza
      occs[occIdx] = { ...occs[occIdx], [field]: value };

      return { ...item, occurrences: occs };
    }));
  };

  /* ------------------------------------------------------------------ */
  /* render                                                             */
  /* ------------------------------------------------------------------ */
  return (
    <div
      className="overlay-container"
      onClick={e => {
        if (e.target === e.currentTarget) handleRequestClose();
      }}
    >
      <div className="overlay-content">
        <button className="close-button" onClick={handleRequestClose} disabled={isSaving}>
          ×
        </button>

        <header
          className="overlay-header"
          style={{ backgroundImage: `url(${program.imageUrl || 'https://via.placeholder.com/200'})` }}
        >
          <h2>Scheduling – {program.title}</h2>
        </header>

        <div className="overlay-body">
          {isEditing ? (
            <>
              <div className="edit-view-toggle">
                <button
                  type="button"
                  className={`summary-toggle ${editViewMode === 'calendar' ? 'active' : ''}`}
                  onClick={() => setEditViewMode('calendar')}
                >
                  Calendario
                </button>
                <button
                  type="button"
                  className={`summary-toggle ${editViewMode === 'list' ? 'active' : ''}`}
                  onClick={() => setEditViewMode('list')}
                >
                  Lista completa
                </button>
              </div>

              {formError && <p className="error">{formError}</p>}

              {editViewMode === 'calendar' ? (
                renderCalendarEdit()
              ) : (
                <div className="program-edit-form">
                  {renderFilterControls('edit')}
                  <div className="schedule-edit-toolbar">
                    <div className="schedule-edit-map">
                      {editMapEntries.length ? (
                        editMapEntries.map(({ item, idx }) => (
                          <button
                            type="button"
                            key={`map-${item.id || idx}`}
                            className={`map-chip ${chipFilterIdx === idx ? 'active' : ''}`}
                            onClick={() => {
                              setChipFilterIdx(prev => {
                                const next = prev === idx ? null : idx;
                                setExpandedIdx(next);
                                return next;
                              });
                            }}
                          >
                            {item.isRecurring
                              ? item.dayOfWeek || 'Ricorrente'
                              : formatDisplayDate(item.date) || `#${idx + 1}`}
                          </button>
                        ))
                      ) : (
                        <span className="map-placeholder">Nessuno scheduling</span>
                      )}
                    </div>
                    {chipFilterIdx !== null && (
                      <button
                        type="button"
                        className="summary-toggle"
                        onClick={() => setChipFilterIdx(null)}
                      >
                        Mostra tutti
                      </button>
                    )}
                    <button type="button" className="add-entry-btn" onClick={addScheduleItem}>
                      + Nuovo Scheduling
                    </button>
                  </div>

                  {formError && <p className="error">{formError}</p>}

                  {visibleEditEntries.length ? visibleEditEntries.map(({ item, idx }) => (
                    <div key={item.id || idx} className={`schedule-item-edit-container ${expandedIdx === idx ? 'open' : ''}`}>
                      <div className="schedule-item-edit-header">
                        <div
                          className="schedule-item-edit-summary"
                          onClick={() => setExpandedIdx(expandedIdx === idx ? null : idx)}
                        >
                          <span className="summary-title">
                            {item.isRecurring ? `Ricorrente: ${item.dayOfWeek || 'N/D'}` : formatDisplayDate(item.date)}
                          </span>
                          <span className="summary-time">
                            {item.startTime || '??'} - {item.endTime || '??'}
                          </span>
                        </div>
                        <div className="schedule-item-edit-actions">
                          <span className="badge-priority">P{item.priority ?? 0}</span>
                          {item.isRecurring && <span className="badge-recurring">Ricorrente</span>}
                          <button
                            type="button"
                            className="toggle-entry-btn"
                            onClick={() => setExpandedIdx(expandedIdx === idx ? null : idx)}
                          >
                            {expandedIdx === idx ? 'Nascondi' : 'Modifica'}
                          </button>
                          <button
                            className="delete-schedule-btn schedule-item-edit-delete"
                            onClick={() => handleDeleteSchedule(item.id || null, idx)}
                          >
                            ×
                          </button>
                        </div>
                      </div>

                      {expandedIdx === idx && (
                        <div className="schedule-item-edit-body">
                          <section className="edit-section">
                            <div className="section-header">
                              <h5>Impostazioni base</h5>
                              </div>
                            <div className="field-grid">
                              <label className="field-block checkbox-field">
                                <span>Ricorrente</span>
                                <input
                                  type="checkbox"
                                  checked={item.isRecurring}
                                  onChange={e => mutateScheduleItem(idx, curr => ({ ...curr, isRecurring: e.target.checked }))}
                                />
                              </label>
                              <label className="field-block">
                                <span>Priorità</span>
                                <input
                                  type="number"
                                  min={0}
                                  value={item.priority ?? 0}
                                  onChange={e => mutateScheduleItem(idx, curr => ({ ...curr, priority: +e.target.value || 0 }))}
                                />
                              </label>
                              <label className="field-block">
                                <span>Ora inizio</span>
                                <input
                                  type="time"
                                  value={item.startTime || ''}
                                  onChange={e => mutateScheduleItem(idx, curr => ({ ...curr, startTime: e.target.value }))}
                                />
                              </label>
                              <label className="field-block">
                                <span>Ora fine</span>
                                <input
                                  type="time"
                                  value={item.endTime || ''}
                                  onChange={e => mutateScheduleItem(idx, curr => ({ ...curr, endTime: e.target.value }))}
                                />
                              </label>
                            </div>
                            <label className="field-block full">
                              <span>{item.isRecurring ? 'Giorno ricorrenza' : 'Data evento'}</span>
                              {item.isRecurring ? (
                                <select
                                  value={item.dayOfWeek}
                                  onChange={e => mutateScheduleItem(idx, curr => ({ ...curr, dayOfWeek: e.target.value }))}
                                  required
                                >
                                  <option value="">Seleziona il giorno</option>
                                  {daysIT.map(d => (
                                    <option key={d} value={d}>
                                      {d}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <input
                                  type="date"
                                  value={item.date ? toYMD(item.date) : ''}
                                  onChange={e => mutateScheduleItem(idx, curr => ({ ...curr, date: e.target.value }))}
                                  required
                                />
                              )}
                            </label>
                            <label className="field-block full">
                              <span>Descrizione</span>
                              <textarea
                                value={item.description || ''}
                                onChange={e => mutateScheduleItem(idx, curr => ({ ...curr, description: e.target.value }))}
                                placeholder="Obiettivo editoriale o note interne"
                              />
                            </label>
                            {item.isRecurring && (
                              <div className="field-block full datepicker-field">
                                <span>Date da escludere</span>
                                <DatePicker
                                  multiple
                                  editable={false}
                                  format="YYYY-MM-DD"
                                  value={item.blacklistDates}
                                  inputClass="picker-input"
                                  inputProps={{ readOnly: true }}
                                  className="bg-dark"
                                  calendarPosition="bottom-center"
                                  onChange={dates =>
                                    mutateScheduleItem(idx, curr => ({
                                      ...curr,
                                      blacklistDates: dates.filter(Boolean).map(d => d.format('YYYY-MM-DD')),
                                    }))
                                  }
                                />
                              </div>
                            )}
                          </section>

                          <section className="edit-section">
                            <div className="section-header">
                              <h5>Partecipanti & argomenti</h5>
                              </div>
                            <div className="field-stack">
                              <label>Ospiti</label>
                              <Select
                                className="react-select"
                                classNamePrefix="select"
                                isMulti
                                options={peopleOptions}
                                filterOption={personOptionMatches}
                                noOptionsMessage={noPeopleOptionsMessage}
                                value={(item.guests || [])
                                  .map(id => peopleOptionById.get(id))
                                  .filter(Boolean)}
                                placeholder="Cerca o aggiungi ospiti..."
                                onChange={(selected) => {
                                  const nextGuests = Array.isArray(selected)
                                    ? selected.map(opt => opt.value)
                                    : [];
                                  mutateScheduleItem(idx, curr => ({
                                    ...curr,
                                    guests: nextGuests,
                                  }));
                                }}
                                menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                              />
                            </div>

                            <div className="field-stack">
                              <label>Topics</label>
                              <Select
                                className="react-select"
                                classNamePrefix="select"
                                isMulti
                                options={topicOptions}
                                value={(item.topics || [])
                                  .map(id => topicOptionById.get(id))
                                  .filter(Boolean)}
                                placeholder="Aggiungi topic..."
                                onChange={(selected) => {
                                  const nextTopics = Array.isArray(selected)
                                    ? selected.map(opt => opt.value)
                                    : [];
                                  mutateScheduleItem(idx, curr => ({
                                    ...curr,
                                    topics: nextTopics,
                                  }));
                                }}
                                menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                              />
                            </div>
                          </section>

                          <section className="edit-section">
                            <div className="section-header">
                              <h5>Fasce orarie di default</h5>
                              </div>
                            {renderSlotEditors(item.timeSlots || [], idx)}
                          </section>

                          {item.isRecurring && (
                            <section className="edit-section">
                              <div className="section-header occurrence-toolbar">
                                <div>
                                  <h5>Occorrenze personalizzate</h5>
                                  </div>
                                <button type="button" className="add-entry-btn" onClick={() => addOccurrence(idx)}>
                                  + Nuova occorrenza
                                </button>
                              </div>
                              {item.occurrences?.length ? (
                                item.occurrences.map((occ, occIdx) => {
                                  const dateGuard = getDateInputGuard(item.dayOfWeek);
                                  const occDateValid = isDateAllowedForSchedule(item, occ.date);
                                  return (
                                    <div key={occ.id || `occ-${occIdx}`} className="occurrence-card">
                                      <div className="occurrence-card-header">
                                        <strong>Occorrenza {occIdx + 1}</strong>
                                        <button
                                          type="button"
                                          className="remove-chip"
                                          onClick={() => removeOccurrence(idx, occIdx)}
                                        >
                                          ×
                                        </button>
                                      </div>
                                      <div className="occurrence-grid">
                                        <label>
                                          Data:
                                          <input
                                            type="date"
                                            value={occ.date ? toYMD(occ.date) : ''}
                                            onChange={e => updateOccurrenceField(idx, occIdx, 'date', e.target.value)}
                                            {...(dateGuard || {})}
                                            style={{ borderColor: occDateValid ? undefined : '#ff6b6b' }}
                                          />
                                        </label>
                                        <label>
                                          Ora inizio:
                                          <input
                                            type="time"
                                            value={occ.startTime || ''}
                                            min={item.startTime || undefined}
                                            max={item.endTime || undefined}
                                            onChange={e => updateOccurrenceField(idx, occIdx, 'startTime', e.target.value)}
                                          />
                                        </label>
                                        <label>
                                          Ora fine:
                                          <input
                                            type="time"
                                            value={occ.endTime || ''}
                                            min={item.startTime || undefined}
                                            max={item.endTime || undefined}
                                            onChange={e => updateOccurrenceField(idx, occIdx, 'endTime', e.target.value)}
                                          />
                                        </label>
                                        <label>
                                          Nota:
                                          <input
                                            type="text"
                                            value={occ.note || ''}
                                            onChange={e => updateOccurrenceField(idx, occIdx, 'note', e.target.value)}
                                            placeholder="Annotazioni"
                                          />
                                        </label>
                                      </div>
                                      <div className="occurrence-slot-wrapper">
                                        <h5>Fasce orarie della giornata</h5>
                                        {renderSlotEditors(occ.timeSlots || [], idx, 'occurrence', occIdx)}
                                      </div>
                                      {/* Protagonisti override per questa occorrenza */}
                                      <div className="occurrence-protagonists-wrapper">
                                        <h5>Protagonisti giornata</h5>
                                        <Select
                                          className="react-select"
                                          classNamePrefix="select"
                                          isMulti
                                          options={peopleOptions}
                                          filterOption={personOptionMatches}
                                          noOptionsMessage={() => 'Nessun risultato'}
                                          value={(occ.protagonists || [])
                                            .map(pid => peopleOptionById.get(pid))
                                            .filter(Boolean)}
                                          placeholder="Cerca protagonisti..."
                                          onChange={(selected) => {
                                            const nextProtagonists = Array.isArray(selected)
                                              ? selected.map(opt => opt.value)
                                              : [];
                                            mutateScheduleItem(idx, (scheduleItem) => ({
                                              ...scheduleItem,
                                              occurrences: scheduleItem.occurrences.map((o, oIdx) =>
                                                oIdx === occIdx ? { ...o, protagonists: nextProtagonists } : o
                                              ),
                                            }));
                                          }}
                                          menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                                        />
                                        {program?.protagonists?.length > 0 && (
                                          <button
                                            type="button"
                                            className="reset-protagonists-btn"
                                            onClick={() => {
                                              const programProtagonists = toIdArray(program.protagonists);
                                              mutateScheduleItem(idx, (scheduleItem) => ({
                                                ...scheduleItem,
                                                occurrences: scheduleItem.occurrences.map((o, oIdx) =>
                                                  oIdx === occIdx ? { ...o, protagonists: programProtagonists } : o
                                                ),
                                              }));
                                            }}
                                          >
                                            Ripristina dal programma
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })
                              ) : (
                                <p className="time-slot-empty">Nessuna occorrenza personalizzata.</p>
                              )}
                            </section>
                          )}
                        </div>
                      )}
                    </div>
                  )) : (
                    <p className="time-slot-empty">Nessuno scheduling nel periodo selezionato.</p>
                  )}
                </div>
              )}

              <div className="modal-actions">
                <button
                  type="button"
                  className="save-edit-btn"
                  onClick={handleSaveSchedule}
                  disabled={isSaving}
                  aria-busy={isSaving}
                >
                  {isSaving ? 'Salvataggio…' : 'Salva Scheduling'}
                </button>
                <button type="button" className="cancel-edit-btn" disabled={isSaving} onClick={() => {
                  // Ripristina i dati originali dal props schedules
                  const data = schedules
                    .filter(s => +s.programId === +program.id)
                    .map(s => ({
                      ...s,
                      date: toYMD(s.date),
                      guests: s.guests ? s.guests.map(g => g.id) : [],
                      topics: s.topics ? s.topics.map(t => t.id) : [],
                      isRecurring: !!s.isRecurring,
                      dayOfWeek: s.dayOfWeek || '',
                      description: s.description || '',
                      priority: s.priority ?? 0,
                      blacklistDates: s.blacklistDates ? s.blacklistDates.map(b => toYMD(b.blacklistDate)) : [],
                      timeSlots: Array.isArray(s.timeSlots) ? cloneSlots(s.timeSlots) : [],
                      occurrences: Array.isArray(s.occurrences)
                        ? s.occurrences.map(occ => ({
                            ...occ,
                            date: toYMD(occ.date),
                            startTime: occ.startTime || '',
                            endTime: occ.endTime || '',
                            note: occ.note || '',
                            timeSlots: Array.isArray(occ.timeSlots) ? cloneSlots(occ.timeSlots) : [],
                            protagonists: toIdArray(occ.protagonists || []),
                          }))
                        : [],
                    }));
                  setScheduleData(data);
                  setIsEditing(false);
                }}>
                  Annulla
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="edit-view-toggle">
                <button
                  type="button"
                  className={`summary-toggle ${viewCalendarMode ? 'active' : ''}`}
                  onClick={() => setViewCalendarMode(true)}
                >
                  Calendario
                </button>
                <button
                  type="button"
                  className={`summary-toggle ${!viewCalendarMode ? 'active' : ''}`}
                  onClick={() => setViewCalendarMode(false)}
                >
                  Lista
                </button>
              </div>

              {viewCalendarMode ? (
                renderViewCalendar()
              ) : (
                <>
                  {renderFilterControls('view')}

                  <div className="modal-section compact">
                    <h4>Ricorrenti</h4>
                    {visibleRecurringNonEditorial.length ? (
                      visibleRecurringNonEditorial.map(entry => renderViewCard(entry, 'recurring'))
                    ) : (
                      <p className="time-slot-empty">Nessuno scheduling ricorrente.</p>
                    )}
                  </div>

                    <div className="modal-section compact">
                      <h4>Eventi singoli</h4>
                      {visibleSinglesNonEditorial.length ? (
                        visibleSinglesNonEditorial.map(entry => renderViewCard(entry, 'single'))
                      ) : (
                        <p className="time-slot-empty">
                          {viewFilter === 'all'
                            ? 'Nessun evento singolo disponibile.'
                            : 'Nessun evento singolo nel periodo selezionato.'}
                        </p>
                      )}
                    </div>
                </>
              )}

              <div className="modal-actions">
                <button onClick={() => {
                  // Sincronizza la modalità vista (calendario/lista) tra view e edit
                  setEditViewMode(viewCalendarMode ? 'calendar' : 'list');
                  // Sincronizza il giorno selezionato in edit con quello in view
                  if (viewCalendarMode && viewDay) {
                    setSelectedDay(viewDay);
                  }
                  setIsEditing(true);
                }}>Modifica Scheduling</button>
              </div>
            </>
          )}
        </div>

        {detailPerson && (
          <PersonDetailModal
            person={detailPerson}
            onClose={() => setDetailPerson(null)}
          />
        )}
      </div>
    </div>
  );
};

export default ScheduleOverlay;
